import React from 'react';
import HeroPlaceholder from '../components/HeroPlaceholder';
import ProductPhotoSpec from '../components/ProductPhotoSpec';
import FaqAccordion from '../components/FaqAccordion';
import MattingCalculator from '../components/MattingCalculator';
import CtaBand from '../components/CtaBand';
import SEO from '../components/SEO';
import AeoCallout from '../components/AeoCallout';
import { HERO_MEDIA, PRODUCT_MEDIA } from '../config/media';
import {
    ACCESS_MATS_HUB,
    ACCESS_MAT_SPEC,
    ACCESS_FAQS,
} from '../data/copy';
import { usePageContent } from '../lib/useContent';
import {
    orgJsonLd,
    productJsonLd,
    breadcrumbJsonLd,
    faqJsonLd,
} from '../lib/seo';

const ACCESS_HERO_FALLBACK = {
    hero: {
        eyebrow: ACCESS_MATS_HUB.eyebrow,
        headline: ACCESS_MATS_HUB.title,
        body: ACCESS_MATS_HUB.subtitle,
        image_url: HERO_MEDIA.aerialCorridor.src,
    },
};

export default function AccessMats() {
    const content = usePageContent('products.access-mats', ACCESS_HERO_FALLBACK);
    const hero = content.hero || ACCESS_HERO_FALLBACK.hero;
    return (
        <>
            <SEO
                title="Access Mats for Ground Protection - Muskeg, Ice Road & ROW"
                description="Centurion 3-ply interlocking timber access mats for ground protection - milled in BC for muskeg, ice road and pipeline ROW work. 12″ pockets, 477 mats / km, 25 per corner."
                cmsSlug="seo.access-mats"
                path="/access-mats"
                image={HERO_MEDIA.aerialCorridor.src}
                jsonLd={[
                    orgJsonLd(),
                    productJsonLd({
                        name: 'Centurion Access Mats',
                        description:
                            'Three-ply interlocking timber access mats for ground protection in muskeg, ice road and right-of-way applications. 12\u2033 pockets, 8 ft \u00d7 14 ft \u00d7 5 in standard.',
                        slug: '/access-mats',
                        material: 'Coastal Hem-Fir, #2 & BTR structural grade',
                        additionalProperty: [
                            { name: 'Width', value: 8, unitText: 'FT' },
                            { name: 'Length', value: 14, unitText: 'FT' },
                            { name: 'Thickness', value: 5, unitText: 'IN' },
                            { name: 'Interlocking Fingers', value: '10 in' },
                            { name: 'Bolt Pattern', value: '177-bolt interlocking' },
                            { name: 'Coverage', value: '477 mats per kilometre + 25 per corner' },
                        ],
                    }),
                    breadcrumbJsonLd([
                        { name: 'Home', path: '/' },
                        { name: 'Access Mats', path: '/access-mats' },
                    ]),
                    faqJsonLd(ACCESS_FAQS),
                ]}
            />

            <HeroPlaceholder
                mediaKey="aerialCorridor"
                imageUrl={hero.image_url}
                eyebrow={hero.eyebrow}
                title={hero.headline}
                subtitle={hero.body}
            />

            <ProductPhotoSpec
                testId="access-mat-photo-spec"
                photo={{
                    src: PRODUCT_MEDIA.accessMat.src,
                    alt: PRODUCT_MEDIA.accessMat.alt,
                    caption: '3-ply interlocking access mat - 8\u2032 \u00d7 14\u2032 deck.',
                }}
                specTitle="Standard Centurion access mat."
                specBody="Standard build for the majority of Western Canadian projects. Custom dimensions available - contact the mill."
                specRows={ACCESS_MAT_SPEC}
                specTableTitle="Access Mat - Standard"
            />

            <section className="mx-auto max-w-4xl px-6 py-20 md:py-28">
                <span className="eyebrow">Overview</span>
                <h2 className="h-display mt-6 text-3xl sm:text-4xl">
                    The workhorse of Western Canadian heavy industry.
                </h2>
                <div className="mt-8 space-y-6 font-sans text-base leading-relaxed text-brand-muted">
                    {ACCESS_MATS_HUB.intro.map((p) => (
                        <p key={p.slice(0, 60)}>{p}</p>
                    ))}
                </div>
                <div className="mt-10 grid gap-4 md:grid-cols-2">
                    <AeoCallout
                        testId="aeo-access-per-km"
                        q="How many access mats per kilometre?"
                        a="On a standard 8 ft × 14 ft Centurion access mat lay-down, plan for roughly 477 mats per kilometre of corridor, plus 25 mats per 90° corner for turn-out and equipment swing. Final count varies with mat width, overlap and route geometry - we'll tighten the estimate against your KMZ."
                    />
                    <AeoCallout
                        testId="aeo-access-dimensions"
                        q="What are the standard dimensions of a Centurion access mat?"
                        a={'A standard Centurion access mat is 8 ft \u00d7 14 ft \u00d7 5 in - a 3-ply interlocking timber deck of Coastal Hem-Fir Hi-Line #2, milled in Chemainus, BC, with 12\u2033 pockets to mate with your existing inventory. Each mat covers 112 sq ft. Approximately 55 mats fit on a standard highway truck.'}
                    />
                </div>
            </section>

            <section className="mx-auto max-w-7xl px-6 py-20 md:px-10 md:py-28">
                <div className="grid gap-10 md:grid-cols-[1fr_1.4fr] md:gap-16">
                    <div>
                        <span className="eyebrow">Common Applications</span>
                        <h3 className="h-display mt-6 text-2xl sm:text-3xl">
                            Where Centurion access mats earn their keep.
                        </h3>
                    </div>
                    <ul className="grid gap-3 sm:grid-cols-2">
                        {ACCESS_MATS_HUB.applications.map((a) => (
                            <li
                                key={a}
                                className="flex items-start gap-3 border border-brand-hairline bg-brand-surface p-4 font-sans text-sm text-brand-text"
                            >
                                <span
                                    aria-hidden="true"
                                    className="mt-1.5 h-1.5 w-1.5 flex-none bg-brand-gold"
                                />
                                {a}
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            <section className="border-y border-brand-hairline bg-brand-base">
                <div className="mx-auto max-w-5xl px-6 py-20 md:py-28">
                    <span className="eyebrow">Field Calculator</span>
                    <h2 className="h-display mt-6 text-3xl sm:text-4xl">
                        Estimate your access mat order.
                    </h2>
                    <p className="mt-5 max-w-2xl font-sans text-base text-brand-muted">
                        Compact access-corridor estimator. Enter route distance and corner
                        count below for an instant mat-count estimate.
                    </p>
                    <div className="mt-10">
                        <MattingCalculator variant="compact" />
                    </div>
                </div>
            </section>

            <section className="mx-auto max-w-4xl px-6 py-20 md:py-28">
                <span className="eyebrow">FAQ</span>
                <h2 className="h-display mt-6 text-3xl sm:text-4xl">
                    Common questions about access mats.
                </h2>
                <div className="mt-10">
                    <FaqAccordion faqs={ACCESS_FAQS} />
                </div>
            </section>

            <CtaBand
                eyebrow="Manufacturer Direct"
                title="Need a quote on a specific corridor?"
                body="Send us your route distance and corner count. We'll spec the mat order and lead time."
                cta={{ to: '/quote', label: 'Request a Quote' }}
                secondary={{ to: '/contact', label: 'Talk to Sales' }}
            />
        </>
    );
}
